import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  DatePicker,
  Select,
  Button,
  Statistic,
  Typography,
  Space,
  Alert,
  Progress,
  Empty,
  Table,
  Tag
} from 'antd';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend
} from 'recharts';

import dayjs from 'dayjs';
import { costApi } from '../services/costApi';
import { useTheme } from '../context/ThemeContext';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

const toCostRows = (period) => (Array.isArray(period?.Costs) ? period.Costs : []);

const toCostAmount = (value) => {
  const amount = Number.parseFloat(value);
  return Number.isFinite(amount) ? amount : 0;
};

const buildPeriodBreakdown = (period) => {
  const costs = toCostRows(period);
  const periodTotal = costs.reduce((sum, cost) => sum + toCostAmount(cost?.Amount), 0);

  return costs
    .map((cost) => {
      const amount = toCostAmount(cost?.Amount);
      return {
        service: cost?.Service || 'Unknown',
        cost: amount,
        percentage: periodTotal > 0 ? (amount / periodTotal) * 100 : 0
      };
    })
    .sort((a, b) => b.cost - a.cost);
};

const buildPeriodTableRows = (period) =>
  toCostRows(period).map((cost, index) => {
    const service = cost?.Service || 'Unknown';
    return {
      key: `${service}-${index}`,
      service,
      cost: toCostAmount(cost?.Amount),
      unit: cost?.Unit || 'USD'
    };
  });

const CostMonitor = () => {
  const { isDark } = useTheme();
  const [loading, setLoading] = useState(false);
  const [costData, setCostData] = useState([]);
  const [dateRange, setDateRange] = useState([
    dayjs().subtract(30, 'days'),
    dayjs()
  ]);
  const [granularity, setGranularity] = useState('DAILY');
  const [error, setError] = useState(null);
  const [summaryStats, setSummaryStats] = useState({
    totalCost: 0,
    averageDailyCost: 0,
    topService: '',
    topServiceCost: 0,
    costTrend: 'stable',
    costChange: 0,
    budgetUtilization: 0,
    costEfficiency: 'good'
  });
  const [costAlerts, setCostAlerts] = useState([]);
  const [serviceBreakdown, setServiceBreakdown] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [allServices, setAllServices] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [periodOptions, setPeriodOptions] = useState([]);

  // Define custom color palette
  const colorPalette = [
    '#2BB7DC',
    '#6FD195', 
    '#FFAE4C',
    '#537FF1',
    '#FF6B9D',
    '#C77DFF',
    '#26C485',
    '#FF8C42',
    '#4A90E2',
    '#F39C12'
  ];

  const getServiceColor = (index) => {
    return colorPalette[index % colorPalette.length];
  };

  const fetchCostData = async () => {
    if (!dateRange || dateRange.length !== 2) {
      setError('Please select a valid date range');
      return;
    }

    if (!dateRange || !dateRange[0] || !dateRange[1]) {
      setError('Please select a valid date range');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const startDate = dateRange[0].format('YYYY-MM-DD');
      const endDate = dateRange[1].format('YYYY-MM-DD');
      
      const data = await costApi.getPlatformCosts(startDate, endDate, granularity);
      setCostData(data);
      
      calculateSummaryStats(data);
      
      generateInsights(data);
      
    } catch (err) {
      console.error('Error fetching cost data:', err);
      setError(`Failed to fetch cost data: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const calculateSummaryStats = (data) => {
    if (!data || data.length === 0) {
      setSummaryStats({
        totalCost: 0,
        averageDailyCost: 0,
        topService: '',
        topServiceCost: 0,
        costTrend: 'stable',
        costChange: 0,
        budgetUtilization: 0,
        costEfficiency: 'good'
      });
      return;
    }

    let totalCost = 0;
    const serviceCosts = {};

    data.forEach(period => {
      toCostRows(period).forEach(cost => {
        const amount = toCostAmount(cost?.Amount);
        totalCost += amount;
        const service = cost?.Service || 'Unknown';
        
        if (serviceCosts[service]) {
          serviceCosts[service] += amount;
        } else {
          serviceCosts[service] = amount;
        }
      });
    });

    const serviceNames = Object.keys(serviceCosts);
    const topService = serviceNames.length
      ? serviceNames.reduce((a, b) => serviceCosts[a] > serviceCosts[b] ? a : b)
      : '';

    const daysDiff = dateRange[1].diff(dateRange[0], 'days') + 1;
    const averageDailyCost = totalCost / daysDiff;

    let costChange = 0;
    if (data.length >= 2) {
      const firstPeriodTotal = toCostRows(data[0]).reduce((sum, cost) => sum + toCostAmount(cost?.Amount), 0);
      const lastPeriodTotal = toCostRows(data[data.length - 1]).reduce((sum, cost) => sum + toCostAmount(cost?.Amount), 0);
      costChange = firstPeriodTotal > 0 ? ((lastPeriodTotal - firstPeriodTotal) / firstPeriodTotal) * 100 : 0;
    }

    let costTrend = 'stable';
    if (costChange > 10) {
      costTrend = 'increasing';
    } else if (costChange < -10) {
      costTrend = 'decreasing';
    }

    const monthlyBudget = 1000;
    const budgetUtilization = (totalCost / monthlyBudget) * 100;

    let costEfficiency = 'good';
    if (budgetUtilization > 80) {
      costEfficiency = 'critical';
    } else if (budgetUtilization > 60) {
      costEfficiency = 'warning';
    }

    setSummaryStats({
      totalCost,
      averageDailyCost,
      topService,
      topServiceCost: serviceCosts[topService] || 0,
      costTrend,
      costChange,
      budgetUtilization,
      costEfficiency
    });

    const breakdown = Object.entries(serviceCosts)
      .map(([service, cost]) => ({ service, cost, percentage: totalCost > 0 ? (cost / totalCost) * 100 : 0 }))
      .sort((a, b) => b.cost - a.cost);
    
    // Don't set aggregated breakdown if granularity is DAILY - we'll set it from selected period
    if (granularity !== 'DAILY') {
      setServiceBreakdown(breakdown);
    }

    const uniqueServices = [...new Set(data.flatMap(period => 
      toCostRows(period).map(cost => cost?.Service).filter(Boolean)
    ))].sort();
    setAllServices(uniqueServices);

    const periods = data.map(period => ({
      value: period.Period,
      label: period.Period,
      costs: toCostRows(period)
    }));
    setPeriodOptions(periods);

    // For DAILY granularity, always use the first period's data
    if (granularity === 'DAILY') {
      // Ensure we have a valid period for daily view
      const firstPeriod = periods[0]?.value;
      if (firstPeriod) {
        // Set selectedPeriod to first period if not set or if it doesn't exist in current periods
        const currentPeriod = periods.find(p => p.value === selectedPeriod);
        if (!selectedPeriod || !currentPeriod) {
          setSelectedPeriod(firstPeriod);
        }
        
        // Use the selected period (or first period as fallback)
        const periodToUse = selectedPeriod && periods.find(p => p.value === selectedPeriod) 
          ? selectedPeriod 
          : firstPeriod;
        
        const selectedPeriodData = data.find(p => p.Period === periodToUse);
        if (selectedPeriodData) {
          setServiceBreakdown(buildPeriodBreakdown(selectedPeriodData));
          setTableData(buildPeriodTableRows(selectedPeriodData));
        }
      }
    } else {
      // For MONTHLY granularity, use aggregated breakdown
      // Set selectedPeriod to first period if not set
      if (periods.length > 0 && !selectedPeriod) {
        setSelectedPeriod(periods[0].value);
      }
      
      const tableRows = breakdown.map(service => ({
        key: service.service,
        service: service.service,
        cost: service.cost,
        percentage: service.percentage,
        unit: 'USD'
      }));
      setTableData(tableRows);
    }
  };

  const generateInsights = (data) => {
    setCostAlerts([]);
  };

  useEffect(() => {
    fetchCostData();
  }, []);

  useEffect(() => {
    if (dateRange[0] && dateRange[1]) {
      // Reset selectedPeriod when granularity changes to ensure fresh data
      setSelectedPeriod(null);
      fetchCostData();
    }
  }, [dateRange, granularity]);

  // Update breakdown when switching to DAILY granularity
  useEffect(() => {
    if (costData.length > 0 && granularity === 'DAILY' && periodOptions.length > 0) {
      // If selectedPeriod doesn't exist in current periods, use first period
      const currentPeriod = periodOptions.find(p => p.value === selectedPeriod);
      if (!selectedPeriod || !currentPeriod) {
        const firstPeriod = periodOptions[0]?.value;
        if (firstPeriod) {
          setSelectedPeriod(firstPeriod);
        }
      }
    }
  }, [costData, granularity, periodOptions]);

  const handlePeriodChange = (period) => {
    setSelectedPeriod(period);
    
    if (granularity === 'DAILY') {
      // For daily granularity, update both table and breakdown
      const selectedPeriodData = costData.find(p => p.Period === period);
      if (selectedPeriodData) {
        setServiceBreakdown(buildPeriodBreakdown(selectedPeriodData));
        setTableData(buildPeriodTableRows(selectedPeriodData));
      }
    } else if (granularity === 'MONTHLY') {
      // For monthly granularity, update table only (breakdown is aggregated)
      const selectedPeriodData = costData.find(p => p.Period === period);
      if (selectedPeriodData) {
        setTableData(buildPeriodTableRows(selectedPeriodData));
        setServiceBreakdown(buildPeriodBreakdown(selectedPeriodData));
      }
    }
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'increasing':
        return ;
      case 'decreasing':
        return ;
      default:
        return ;
    }
  };

  const getTrendColor = (trend) => {
    switch (trend) {
      case 'increasing':
        return '#f5222d';
      case 'decreasing':
        return '#52c41a';
      default:
        return '#1890ff';
    }
  };

  const getEfficiencyColor = (efficiency) => {
    switch (efficiency) {
      case 'critical':
        return '#f5222d';
      case 'warning':
        return '#fa8c16';
      default:
        return '#52c41a';
    }
  };

  return (
    <div style={{ padding: 0, background: 'transparent', height: '100vh', overflow: 'auto' }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16, padding: '16px 24px' }}>
        <Col>
          <Title level={3} style={{ margin: 0, color: isDark ? "#e6edf3" : undefined }}>
            Cost Monitor
          </Title>
        </Col>
        <Col>
          <Space size="large">
            <Space direction="vertical" size={2}>
              <Text strong style={{ fontSize: '12px', color: isDark ? "#e6edf3" : undefined }}>Date Range</Text>
              <RangePicker
                value={dateRange}
                onChange={(dates) => {
                  if (dates && dates[0] && dates[1]) {
                    setDateRange(dates);
                  }
                }}
                format="YYYY-MM-DD"
                size="small"
                style={{ 
                  width: 300,
                  background: isDark ? '#0d1117' : undefined,
                  borderColor: isDark ? '#30363d' : undefined,
                }}
                allowClear={false}
              />
            </Space>
            <Space direction="vertical" size={2}>
              <Text strong style={{ fontSize: '12px', color: isDark ? "#e6edf3" : undefined }}>Granularity</Text>
              <Select
                value={granularity}
                onChange={setGranularity}
                size="small"
                style={{ 
                  width: 140,
                  background: isDark ? '#0d1117' : undefined,
                  borderColor: isDark ? '#30363d' : undefined,
                }}
                dropdownStyle={{ background: isDark ? '#161b22' : undefined }}
              >
                <Option value="DAILY">Daily</Option>
                <Option value="MONTHLY">Monthly</Option>
              </Select>
            </Space>
          </Space>
        </Col>
      </Row>

      {error && (
        <Alert
          message={error}
          type="error"style={{ marginBottom: 16, marginLeft: 24, marginRight: 24 }}
          closable
          onClose={() => setError(null)}
        />
      )}

      {costAlerts.length > 0 && (
        <div style={{ marginBottom: 16, marginLeft: 24, marginRight: 24 }}>
          {costAlerts.map((alert, index) => (
            <Alert
              key={index}
              message={alert.title}
              description={alert.message}
              type={alert.type}
              style={{ marginBottom: 8 }}size="small"
            />
          ))}
        </div>
      )}

      <Row gutter={[16, 16]} style={{ marginBottom: 16, paddingLeft: 24, paddingRight: 24 }}>
        <Col xs={12} sm={6}>
          <Card 
            size="small" 
            className="custom-ant-card cost-monitor-card"
            // style={{
            //   width: '272.25px',
            //   height: '112px',
            //   minWidth: '200px',
            //   borderRadius: '16px',
            //   padding: '24px',
            //   opacity: 1,
            //   gap: '8px',
            //   boxShadow: '-8px -8px 16px 0px #FFFFFF, 8px 8px 16px 0px #C9D9E8'
            // }}
          >
            <Statistic
              title={<span style={{ color: isDark ? "#adbac7" : undefined }}>Total Cost</span>}
              value={summaryStats.totalCost}
              precision={2}
              valueStyle={{ color: '#1890ff', fontSize: '18px' }}
              prefix="$"
            />
            <Text type="secondary" style={{ fontSize: '11px', color: isDark ? "#6e7681" : undefined }}>
              {summaryStats.costChange > 0 ? '+' : ''}{summaryStats.costChange.toFixed(1)}%
            </Text>
          </Card>
          
        </Col>
        <Col xs={12} sm={6}>
          <Card 
            size="small" 
            className="custom-ant-card cost-monitor-card"
            // style={{
            //   width: '272.25px',
            //   height: '112px',
            //   minWidth: '200px',
            //   borderRadius: '16px',
            //   padding: '24px',
            //   opacity: 1,
            //   gap: '8px',
            //   boxShadow: '-8px -8px 16px 0px #FFFFFF, 8px 8px 16px 0px #C9D9E8'
            // }}
          >
            <Statistic
              title={<span style={{ color: isDark ? "#adbac7" : undefined }}>Daily Avg</span>}
              value={summaryStats.averageDailyCost}
              precision={2}
              valueStyle={{ color: '#52c41a', fontSize: '18px' }}
              prefix="$"
            />
            <Text type="secondary" style={{ fontSize: '11px', color: isDark ? "#6e7681" : undefined }}>
              ${(summaryStats.averageDailyCost * 30).toFixed(0)}/month
            </Text>
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card 
            size="small" 
            className="custom-ant-card cost-monitor-card"
            // style={{
            //   width: '272.25px',
            //   height: '112px',
            //   minWidth: '200px',
            //   borderRadius: '16px',
            //   padding: '24px',
            //   opacity: 1,
            //   gap: '8px',
            //   boxShadow: '-8px -8px 16px 0px #FFFFFF, 8px 8px 16px 0px #C9D9E8'
            // }}
          >
            <Statistic
              title={<span style={{ color: isDark ? "#adbac7" : undefined }}>Budget</span>}
              value={summaryStats.budgetUtilization}
              precision={1}
              suffix="%"
              valueStyle={{ color: getEfficiencyColor(summaryStats.costEfficiency), fontSize: '18px' }}
            />
            <Progress 
              percent={summaryStats.budgetUtilization} 
              size="small" 
              showInfo={false}
              status={summaryStats.costEfficiency === 'critical' ? 'exception' : 'normal'}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card 
            size="small" 
            className="custom-ant-card cost-monitor-card"
            // style={{
            //   width: '272.25px',
            //   height: '112px',
            //   minWidth: '200px',
            //   borderRadius: '16px',
            //   padding: '24px',
            //   opacity: 1,
            //   gap: '8px',
            //   boxShadow: '-8px -8px 16px 0px #FFFFFF, 8px 8px 16px 0px #C9D9E8'
            // }}
          >
            <Statistic
              title={<span style={{ color: isDark ? "#adbac7" : undefined }}>Trend</span>}
              value={summaryStats.costTrend}
              valueStyle={{ color: getTrendColor(summaryStats.costTrend), fontSize: '18px' }}/>
            <Text type="secondary" style={{ fontSize: '11px', color: isDark ? "#6e7681" : undefined }}>
              {summaryStats.costChange > 0 ? '+' : ''}{summaryStats.costChange.toFixed(1)}% change
            </Text>
          </Card>
        </Col>
      </Row>

      <Row gutter={16} style={{ height: '400px', paddingLeft: 24, paddingRight: 24 }}>
        <Col xs={24} lg={12}>
          <Card 
            size="small"
            className="custom-ant-card"
            title={
              <Space>
                <span style={{ color: isDark ? "#e6edf3" : undefined }}>Cost Distribution</span>
              </Space>
            }
            extra={
              <Space>
                {(granularity === 'DAILY' || (granularity === 'MONTHLY' && periodOptions.length > 1)) && (
                  <Select
                    value={selectedPeriod}
                    onChange={handlePeriodChange}
                    style={{ 
                      width: 160,
                      background: isDark ? '#0d1117' : undefined,
                      borderColor: isDark ? '#30363d' : undefined,
                    }}
                    size="small"
                    placeholder={granularity === 'DAILY' ? "Select Day" : "Select Month"}
                    dropdownStyle={{ background: isDark ? '#161b22' : undefined }}
                  >
                    {periodOptions.map(period => (
                      <Option key={period.value} value={period.value}>
                        {granularity === 'DAILY' 
                          ? period.label.split(' → ')[0]
                          : period.label.split(' → ')[0].substring(0, 7)
                        }
                      </Option>
                    ))}
                  </Select>
                )}
              </Space>
            }
            style={{ 
              height: '400px',
              boxShadow: isDark ? '0 2px 8px rgba(0,0,0,0.3)' : '-8px -8px 16px 0px #FFFFFF, 8px 8px 16px 0px #C9D9E8',
              background: isDark ? '#161b22' : undefined,
              border: isDark ? '1px solid #30363d' : undefined,
            }}
            bodyStyle={{ padding: '20px', height: '343px', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', background: isDark ? '#161b22' : undefined }}
          >
            {serviceBreakdown.length > 0 ? (
              <div style={{ width: '100%', height: '100%' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={serviceBreakdown.map(item => ({
                        name: item.service,
                        value: item.cost,
                        percentage: item.percentage
                      }))}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={0}
                      dataKey="value"
                    >
                      {serviceBreakdown.map((entry, index) => (
                        <Cell 
                          key={`cell-${index}`} 
                          fill={getServiceColor(index)}
                        />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value, name) => [`$${value.toFixed(2)}`, name]}
                      labelFormatter={(label) => `Service: ${label}`}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <div style={{ 
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  textAlign: 'center',
                  pointerEvents: 'none'
                }}>
                  <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#1890ff' }}>
                    ${serviceBreakdown.reduce((sum, s) => sum + s.cost, 0).toFixed(2)}
                  </div>
                  <div style={{ fontSize: '12px', color: isDark ? '#adbac7' : '#666', marginTop: '4px' }}>
                    {granularity === 'DAILY' && selectedPeriod ? 'Daily Cost' : 
                     granularity === 'MONTHLY' && selectedPeriod ? 'Monthly Cost' : 
                     'Total Cost'}
                  </div>
                  <div style={{ fontSize: '10px', color: isDark ? '#6e7681' : '#999', marginTop: '2px' }}>
                    {serviceBreakdown.length} Services
                  </div>
                </div>
              </div>
            ) : (
              <Empty description="No cost data" size="small" />
            )}
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card 
            size="small"
            className="custom-ant-card"
            title={
              <Space>
                <span style={{ color: isDark ? "#e6edf3" : undefined }}>Service Data</span>
              </Space>
            }
            extra={
              <Text type="secondary" style={{ fontSize: '12px', color: isDark ? "#adbac7" : undefined }}>
                {serviceBreakdown.length} services
              </Text>
            }
            style={{ 
              height: '400px',
              background: isDark ? '#161b22' : undefined,
              border: isDark ? '1px solid #30363d' : undefined,
            }}
            bodyStyle={{ 
              padding: '16px', 
              height: '343px', 
              overflow: 'auto',
              background: isDark ? '#161b22' : undefined,
            }}
          >
            {serviceBreakdown.length > 0 ? (
              <div>
                {serviceBreakdown.map((service, index) => (
                  <div key={service.service} style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    marginBottom: 8 
                  }}>
                    <div style={{ 
                      width: 12, 
                      height: 12, 
                      backgroundColor: getServiceColor(index),
                      borderRadius: 2,
                      marginRight: 8
                    }}></div>
                    <div style={{ flex: 1 }}>
                      <Text style={{ fontSize: '11px', color: isDark ? "#e6edf3" : undefined }}>{service.service}</Text>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Text strong style={{ fontSize: '12px', color: isDark ? "#e6edf3" : undefined }}>${service.cost.toFixed(2)}</Text>
                        <Text type="secondary" style={{ fontSize: '10px', color: isDark ? "#adbac7" : undefined }}>
                          {service.percentage.toFixed(1)}%
                        </Text>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <Empty description="No service data available" size="small" />
            )}
          </Card>
        </Col>
      </Row>
     </div>
  );
};

export default CostMonitor;
